import React from 'react';
import styled from 'styled-components';
import FormTextGroup from 'components/forms/components/form-text-group';
import colors from 'utils/colors';
import { Icon, Button } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import ReCAPTCHA from 'react-google-recaptcha';

const LoginForm = props => (
	<Wrapper onKeyDown={props.handleKeyDown}>
		<Logo
			src={require('images/logo.png')}
			alt={process.env.REACT_APP_LOGO_ALT_TEXT || 'Logo'}
		/>

		<ContentWrapper>
			{/* Show two password inputs if user is changing password */}
			{props.isChanging ? (
				<React.Fragment>
					<div style={{"marginLeft": "50px", "marginRight": "50px"}}>
						<h3 style={{"marginTop": "0px", "textAlign": "center"}}>Password Requirements</h3>
						<ol>
							<li>Minimum of 8 characters.</li>
							<li>Contain atleast 1 uppercase letter, 1 lowercase letter, 1 number, 1 special character.</li>
							<li>Required special character must include one of the following: @$!%*?&</li>
						</ol>
					</div>
					<FormTextGroup
						label="Old Password"
						name="oldPassword"
						inputType="password"
						leftIcon="lock"
						value={props.oldPassword}
						handleChange={props.handleChange}
						style={{ width: "75%" }}
					/>
					<FormTextGroup
						label="New Password"
						name="newPassword"
						inputType="password"
						leftIcon="lock"
						value={props.newPassword}
						handleChange={props.handleChange}
						style={{ width: "75%" }}
					/>
					<FormTextGroup
						label="Retype Password"
						name="checkPassword"
						inputType="password"
						leftIcon="lock"
						value={props.checkPassword}
						handleChange={props.handleChange}
						style={{ width: "75%" }}
					/>
					<StyledButton
						className="bp3-icon-standard"
						onClick={props.handleChangePassword}
					>
						Change Password <StyledIcon icon={IconNames.ARROW_RIGHT} />
					</StyledButton>
				</React.Fragment>
			) : (
					<React.Fragment>
						{props.isForgot ? "" : 
							<React.Fragment>
								<LoginHeader>Part of the University of Rochester?</LoginHeader>
								<StyledButton
									className="bp3-icon-standard"
									onClick={props.handleShibSubmit}
								>
									Continue <StyledIcon icon={IconNames.ARROW_RIGHT} />
								</StyledButton>
					
								<Separator><span>OR</span></Separator>
							</React.Fragment>
						}
						{/* Show username field if login or forgot password */}
						<LoginHeader style={{marginBottom: "5px"}}>Third Party Login</LoginHeader>
						<FormTextGroup
							label="Username"
							name="username"
							leftIcon="person"
							value={props.username}
							handleChange={props.handleChange}
							style={{ width: "75%" }}
						/>
						{props.isForgot ? (
							<React.Fragment>
								{/* Show reset button if forgot password */}
								<StyledButton
									className="bp3-icon-standard"
									onClick={props.handleReset}
									disabled={props.isSubmitting || props.isFetching}
								>
									Reset <StyledIcon icon={IconNames.ARROW_RIGHT} />
								</StyledButton>
								<StyledButton
									className="bp3-icon-standard"
									onClick={props.handleCancel}
									disabled={props.isSubmitting || props.isFetching}
									primary={colors.red}
									secondary={colors.darkRed}
								>
									Cancel <StyledIcon icon={IconNames.ARROW_LEFT} />
								</StyledButton>
							</React.Fragment>
						) : (
								<React.Fragment>
									{/* Show login form */}
									<FormTextGroup
										label="Password"
										name="password"
										inputType="password"
										leftIcon="lock"
										value={props.password}
										handleChange={props.handleChange}
										style={{ width: "75%" }}
									/>
									{props.errors.isError && (
										<ErrorWrapper>{props.errors.message}</ErrorWrapper>
									)}

									{/* Show Google Captcha */}
									{process.env.REACT_APP_IGNORE_CAPTCHA === "true" ? "" : 
										<ReCAPTCHA
											style={{
												display: 'flex',
												justifyContent: 'center',
											}}
											sitekey={props.sitekey}
											onChange={props.handleReCaptchaChange}
										/>
									}
									{props.captchaErrors.isError && (
										<ErrorWrapper>{props.captchaErrors.message}</ErrorWrapper>
									)}
									<StyledButton
										className="bp3-icon-standard"
										onClick={props.handleSubmit}
										name="loginButton"
										disabled={
											(!props.captchaPass && process.env.REACT_APP_IGNORE_CAPTCHA !== "true") || props.isSubmitting || props.isFetching
										}
									>
										Login <StyledIcon icon={IconNames.ARROW_RIGHT} />
									</StyledButton>
									<Button
										className="bp3-icon-standard"
										minimal={true}
										onClick={props.handleForgot}
										disabled={props.isSubmitting || props.isFetching}
									>
										Forgot Password
									</Button>
								</React.Fragment>
							)}
					</React.Fragment>
				)}
		</ContentWrapper>
	</Wrapper>
);

const Wrapper = styled.div`
  width: 30%;
  min-width: 25rem;
  background: #fff;
  border-radius: 5px;
  padding: 2rem;
  -webkit-box-shadow: 0px 0px 20px 3px rgba(16, 22, 26, 0.2);
  -moz-box-shadow: 0px 0px 20px 3px rgba(16, 22, 26, 0.2);
  box-shadow: 0px 0px 20px 3px rgba(16, 22, 26, 0.2);
  display: flex;
  flex-flow: column wrap;
`;

const ContentWrapper = styled.div`
display: flex;
flex-direction: column;
align-items: center;
`;

const Logo = styled.img`
  width: 15%;
  min-width: 250px;
  height: auto;
  background-repeat: no-repeat;
  background-size: contain;
  margin: 20px auto;
`;

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50px;
  border: 3px solid #fff;
  color: #fff;
  overflow: hidden;
  font-family: inherit;
  font-size: 1rem;
  cursor: pointer;
  padding: 15px 50px;
  text-align: center;

  margin: 5px 0px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
  width: 60%;
  outline: none;
  position: relative;
  -webkit-transition: all 0.3s;
  -moz-transition: all 0.3s;
  transition: all 0.3s;
  align-self: auto;
  background: ${props => props.primary || colors.primary};

  &:hover {
    background: ${props => props.secondary || '#1b89d4'};
  }
  &:active {
    border-color: ${props => props.secondary || colors.secondary};
    color: ${props => props.secondary || colors.secondary};
  }
  &:disabled {
    background: ${colors.lightGray};
    cursor: not-allowed;
  }
`;

const StyledIcon = styled(Icon)`
  left: 70%;
  opacity: 0;
  top: 1rem;
  position: absolute;
  height: 100%;
  font-size: 125%;
  line-height: 3.5;
  color: #fff;
  -webkit-transition: all 0.3s;
  -moz-transition: all 0.3s;
  transition: all 0.3s;

  ${StyledButton}:hover & {
    left: 80%;
    opacity: 1;
  }
  ${StyledButton}:active & {
    left: 80%;
    opacity: 1;
    color: ${colors.secondary};
  }
  ${StyledButton}:disabled & {
    opacity: 0;
  }
`;

const ErrorWrapper = styled.p`
  color: ${colors.darkRed};
`;

const Separator = styled.h2`
	width: 80%;
	text-align: center;
	border-bottom: 1px solid #9E9E9E;
	line-height: 0.01em;
	margin: 40px 0 40px;
	span{
		background: #ffffff;
		padding: 0 10px;
		color: #444444;
		font-size: 12px;
	}
`;

const LoginHeader = styled.h4`
	font-size: 20px;
	margin: 0 0 20px 0;
`;

export default LoginForm;
